import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Chip,
  useDisclosure,
} from "@nextui-org/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar, faSearch } from "@fortawesome/free-solid-svg-icons";
import { getDataWithFilterUrl, UpdateOneData } from "../../controllers/APIController";
import { changeHandler } from "../../utils/helper";
import { StandardCancelButton } from "../../components/StandardButtons";
import { useQueryClient } from "@tanstack/react-query";

const EditEvidenceModal = ({
  isOpen,
  onOpenChange,
  evidenceData,
  setReRenderPage,
  pageContext = "Performance Data",
  fullFilterUrl,
}) => {
  const [editedData, setEditedData] = useState({
    Name: "",
    Description: "",
    Category: "",
    "Related Metric": "",
    "Related Response Data": [],
  });
  const clientQuery= useQueryClient()
  const [metrics, setMetrics] = useState([]);
  const [responses, setResponses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [metricSearchTerm, setMetricSearchTerm] = useState("");
  const [responseSearchTerm, setResponseSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedMetricClass, setSelectedMetricClass] = useState("all");
  const [OrgInfo, setOrgInfo] = useState(null);

  // Fetch organization info
  useEffect(() => {
    const orgId = localStorage.getItem("orgId");
    if (orgId) {
      setOrgInfo(orgId);
    }
  }, []);

  // Initialize data when evidence is provided
  useEffect(() => {
    if (evidenceData) {
      setEditedData({
        Name: evidenceData.attributes?.Name || "",
        Description: evidenceData.attributes?.Description || "",
        Category: evidenceData.attributes?.Category || pageContext,
        "Related Metric": "",
        "Related Response Data":
          evidenceData.attributes?.ReferenceList?.data?.map((ref) => ref.id) ||
          [],
      });
    }
  }, [evidenceData, pageContext]);

  // Fetch and set the related metric based on linked responses
  useEffect(() => {
    const fetchRelatedMetric = async () => {
      if (
        !evidenceData?.attributes?.ReferenceList?.data?.length ||
        !metrics.length
      ) {
        return;
      }

      try {
        // Get the first linked response to determine the metric
        const firstResponseId =
          evidenceData.attributes.ReferenceList.data[0].id;
        const responseData = await getDataWithFilterUrl(
          "responses",
          `filters[id][$eqi]=${firstResponseId}`
        );
        const response = responseData?.data?.[0];

        if (response?.attributes?.Metric_Id) {
          // Find the metric that matches this Metric_Id
          const metricName = response.attributes.Metric_Id;
          const relatedMetric = metrics.find(
            (metric) =>
              metric.attributes?.Name === metricName ||
              metric.attributes?.name === metricName
          );

          if (relatedMetric) {
            setEditedData((prev) => ({
              ...prev,
              "Related Metric": relatedMetric.id.toString(),
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching related metric:", error);
      }
    };

    fetchRelatedMetric();
  }, [evidenceData, metrics]);

  // Fetch metrics
  useEffect(() => {
    const fetchMetrics = async () => {
      if (!OrgInfo) return;

      try {
        const orgFilterUrl = `filters[Organization][$eqi]=${OrgInfo}`;
        const data = await getDataWithFilterUrl("metrics", orgFilterUrl);
        setMetrics(data?.data || []);
      } catch (error) {
        console.error("Error fetching metrics:", error);
      }
    };

    fetchMetrics();
  }, [OrgInfo]);

  // Fetch responses when metric is selected or when editing existing evidence
  useEffect(() => {
    const fetchResponses = async () => {
      // If we have linked responses but no metric selected, fetch them first
      if (
        editedData["Related Response Data"].length > 0 &&
        !editedData["Related Metric"] &&
        evidenceData
      ) {
        try {
          const responseIds = editedData["Related Response Data"];
          const responsePromises = responseIds.map((id) =>
            getDataWithFilterUrl("responses", `filters[id][$eqi]=${id}`)
          );
          const responseResults = await Promise.all(responsePromises);
          const linkedResponses = responseResults
            .map((result) => result?.data?.[0])
            .filter(Boolean);
          setResponses(linkedResponses);
          return;
        } catch (error) {
          console.error("Error fetching linked responses:", error);
        }
      }

      if (!editedData["Related Metric"] || !metrics.length) {
        setResponses([]);
        return;
      }

      try {
        console.log("Selected metric ID:", editedData["Related Metric"]);
        console.log("Available metrics:", metrics);

        // Get the selected metric to extract its name
        const selectedMetric = metrics.find(
          (metric) => metric.id === parseInt(editedData["Related Metric"])
        );
        console.log("Selected metric object:", selectedMetric);

        if (!selectedMetric) {
          console.log("Selected metric not found in metrics list");
          setResponses([]);
          return;
        }

        const metricName =
          selectedMetric.attributes?.Name || selectedMetric.attributes?.name;
        console.log("Extracted metric name:", metricName);

        if (!metricName) {
          console.log("No metric name found in selected metric");
          setResponses([]);
          return;
        }

        // Try both field name variations since the console shows Metric_Id (capitalized)
        let metricFilterUrl = `filters[Metric_Id][$eqi]=${encodeURIComponent(
          metricName
        )}`;
        console.log("Base filter URL:", metricFilterUrl);
        console.log("Metric name being filtered for:", metricName);

        // Add date range filter if specified
        if (startDate && endDate) {
          const startDateObj = new Date(startDate);
          const endDateObj = new Date(endDate);
          endDateObj.setDate(endDateObj.getDate() + 1);

          const startDateStr = startDateObj.toISOString();
          const endDateStr = endDateObj.toISOString();

          metricFilterUrl += `&filters[createdAt][$gte]=${startDateStr}&filters[createdAt][$lt]=${endDateStr}`;
          console.log("With date range filter:", metricFilterUrl);
        } else if (startDate) {
          const startDateObj = new Date(startDate);
          const endDateObj = new Date(startDate);
          endDateObj.setDate(endDateObj.getDate() + 1);

          const startDateStr = startDateObj.toISOString();
          const endDateStr = endDateObj.toISOString();

          metricFilterUrl += `&filters[createdAt][$gte]=${startDateStr}&filters[createdAt][$lt]=${endDateStr}`;
          console.log("With single date filter:", metricFilterUrl);
        } else {
          console.log("No date filter applied");
        }

        console.log("Final filter URL:", metricFilterUrl);
        const data = await getDataWithFilterUrl("responses", metricFilterUrl);
        console.log("Raw API response:", data);

        const responsesList = data?.data || [];
        console.log("Processed responses list:", responsesList);
        console.log("Total responses returned:", responsesList.length);

        // Check the first few responses to see if filtering is working
        if (responsesList.length > 0) {
          console.log(
            "First response Metric_Id:",
            responsesList[0]?.attributes?.Metric_Id
          );
          console.log(
            "Second response Metric_Id:",
            responsesList[1]?.attributes?.Metric_Id
          );
          console.log(
            "Third response Metric_Id:",
            responsesList[2]?.attributes?.Metric_Id
          );
        }

        setResponses(responsesList);
      } catch (error) {
        console.error("Error fetching responses:", error);
        setResponses([]);
      }
    };

    fetchResponses();
  }, [
    editedData["Related Metric"],
    editedData["Related Response Data"],
    startDate,
    endDate,
    metrics,
    evidenceData,
  ]);

  // Get unique metric class names
  const metricClasses = [
    ...new Set(
      metrics.map(
        (metric) =>
          metric.attributes?.ClassName ||
          metric.attributes?.class_name ||
          "Uncategorized"
      )
    ),
  ].sort();

  // Filter metrics based on search term and class name
  const filteredMetrics = metrics.filter((metric) => {
    // Class name filter
    if (selectedMetricClass !== "all") {
      const metricClass =
        metric.attributes?.ClassName ||
        metric.attributes?.class_name ||
        "Uncategorized";
      if (metricClass !== selectedMetricClass) {
        return false;
      }
    }

    // Search term filter
    if (!metricSearchTerm) return true;
    const metricName = metric.attributes?.Name || metric.attributes?.name || "";
    return metricName.toLowerCase().includes(metricSearchTerm.toLowerCase());
  });

  // Filter responses based on search term only (date filtering is done at backend level)
  const filteredResponses = responses.filter((response) => {
    if (!responseSearchTerm) return true;
    const responseName =
      response.attributes?.JsonSchema?.flatData?.Name ||
      response.attributes?.JsonSchema?.flatData?.name ||
      response.attributes?.JsonSchema?.name ||
      response.attributes?.JsonSchema?.Name ||
      "";
    return responseName
      .toLowerCase()
      .includes(responseSearchTerm.toLowerCase());
  });

  const handleMetricChange = (value) => {
    setEditedData((prev) => ({
      ...prev,
      "Related Metric": value,
      "Related Response Data": [], // Reset response selection when metric changes
    }));
    setResponseSearchTerm(""); // Reset response search when metric changes
    setStartDate(""); // Reset date range when metric changes
    setEndDate("");
  };

  const handleMetricClassChange = (value) => {
    setSelectedMetricClass(value);
    // Reset metric selection when class changes
    setEditedData((prev) => ({
      ...prev,
      "Related Metric": "",
      "Related Response Data": [],
    }));
    setMetricSearchTerm("");
    setResponseSearchTerm("");
  };

  const handleResponseSelection = (selectedKeys) => {
    setEditedData((prev) => ({
      ...prev,
      "Related Response Data": Array.from(selectedKeys),
    }));
  };

  const getResponseDisplayName = (response) => {
    // Check for name in the new flatData structure first
    const name =
      response.attributes?.JsonSchema?.flatData?.Name ||
      response.attributes?.JsonSchema?.flatData?.name ||
      response.attributes?.JsonSchema?.name ||
      response.attributes?.JsonSchema?.Name ||
      `Response ${response.id}`;
    const date = new Date(
      response.attributes?.createdAt || response.attributes?.updatedAt
    ).toLocaleDateString();
    return `${name} (${date})`;
  };

  const validationHandler = () => {
    setValidationErrors({});
    let isValid = true;
    const err = {};

    if (editedData["Name"].trim() === "") {
      err["Name"] = "Name is required!";
      isValid = false;
    }

    setValidationErrors(err);
    return isValid;
  };

  const submitHandler = async (onClose) => {
    const validationResponse = validationHandler();
    if (!validationResponse) return;

    setIsLoading(true);

    try {
      // Update the evidence entry
      const updateData = {
        Name: editedData["Name"],
        Description: editedData["Description"],
        Category: editedData["Category"],
      };

      await UpdateOneData("evidences", evidenceData.id, updateData);

      // Update response linkages if metric is selected
      if (
        editedData["Related Metric"] &&
        editedData["Related Response Data"].length > 0
      ) {
        // First, remove this evidence from all existing response ReferenceLists
        const allResponses = await getDataWithFilterUrl("responses", "");
        for (const response of allResponses?.data || []) {
          const existingReferenceList =
            response.attributes.ReferenceList?.data || [];
          const updatedReferenceList = existingReferenceList.filter(
            (ref) => ref.id !== evidenceData.id
          );

          if (updatedReferenceList.length !== existingReferenceList.length) {
            await UpdateOneData("responses", response.id, {
              ReferenceList: updatedReferenceList,
            });
          }
        }

        // Then add this evidence to the selected responses
        for (const responseId of editedData["Related Response Data"]) {
          try {
            const responseData = await getDataWithFilterUrl(
              "responses",
              `filters[id][$eqi]=${responseId}`
            );
            const currentResponse = responseData?.data?.[0];

            if (currentResponse) {
              const existingReferenceList =
                currentResponse.attributes.ReferenceList?.data || [];
              const updatedReferenceList = [
                ...existingReferenceList,
                { id: evidenceData.id },
              ];

              await UpdateOneData("responses", responseId, {
                ReferenceList: updatedReferenceList,
              });
            }
          } catch (error) {
            console.error(`Error updating response ${responseId}:`, error);
          }
        }
      }


    clientQuery.invalidateQueries({ queryKey: ["evidences", fullFilterUrl] });
      onClose();
    } catch (error) {
      console.error("Error updating evidence:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      setValidationErrors({});
      setEditedData({
        Name: "",
        Description: "",
        Category: "",
        "Related Metric": "",
        "Related Response Data": [],
      });
      setResponses([]);
      setMetricSearchTerm("");
      setResponseSearchTerm("");
      setStartDate("");
      setEndDate("");
      setSelectedMetricClass("all");
    }
    onOpenChange(isOpen);
  };

  return (
    <>
      <Modal isOpen={isOpen} onOpenChange={handleOpenChange} size="2xl">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                Edit Evidence
              </ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  <Input
                    label="Name"
                    placeholder="Enter evidence name"
                    value={editedData["Name"]}
                    onChange={(e) =>
                      setEditedData((prev) => ({
                        ...prev,
                        Name: e.target.value,
                      }))
                    }
                    isInvalid={!!validationErrors["Name"]}
                    errorMessage={validationErrors["Name"]}
                    size="sm"
                  />

                  <Input
                    label="Category"
                    value={editedData["Category"]}
                    isReadOnly
                    size="sm"
                  />

                  <Input
                    label="Description"
                    placeholder="Enter description"
                    value={editedData["Description"]}
                    onChange={(e) =>
                      setEditedData((prev) => ({
                        ...prev,
                        Description: e.target.value,
                      }))
                    }
                    size="sm"
                  />

                  <div>
                    <label className="text-sm font-medium text-neutral-700 mb-2 block">
                      Related Metric
                    </label>
                    <div className="space-y-2">
                      <Select
                        placeholder="Filter by metric class"
                        value={selectedMetricClass}
                        onChange={(e) =>
                          handleMetricClassChange(e.target.value)
                        }
                        size="sm"
                        aria-label="Filter by metric class"
                      >
                        <SelectItem
                          key="all"
                          value="all"
                          textValue="All Classes"
                        >
                          All Classes
                        </SelectItem>
                        {metricClasses.map((className) => (
                          <SelectItem
                            key={className}
                            value={className}
                            textValue={className}
                          >
                            {className}
                          </SelectItem>
                        ))}
                      </Select>
                      <Select
                        placeholder="Search and select a metric"
                        value={editedData["Related Metric"]}
                        onChange={(e) => handleMetricChange(e.target.value)}
                        size="sm"
                        onInputChange={setMetricSearchTerm}
                        inputValue={metricSearchTerm}
                        items={filteredMetrics}
                        isDisabled={filteredMetrics.length === 0}
                        aria-label="Search and select a metric"
                      >
                        {(metric) => (
                          <SelectItem
                            key={metric.id}
                            value={metric.id}
                            textValue={
                              metric.attributes?.Name ||
                              metric.attributes?.name ||
                              `Metric ${metric.id}`
                            }
                          >
                            {metric.attributes?.Name ||
                              metric.attributes?.name ||
                              `Metric ${metric.id}`}
                          </SelectItem>
                        )}
                      </Select>
                    </div>
                  </div>

                  {editedData["Related Metric"] && (
                    <div>
                      <label className="text-sm font-medium text-neutral-700 mb-2 block">
                        Related Response Data
                      </label>
                      <p className="text-xs text-neutral-500 mb-2">
                        Select responses for the chosen metric. Use date range
                        to find responses from specific dates.
                      </p>
                      <div className="mb-2 space-y-2">
                        <div className="flex gap-2">
                          <Input
                            type="date"
                            placeholder="Start date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            startContent={
                              <FontAwesomeIcon
                                icon={faCalendar}
                                className="text-neutral-400 text-sm"
                              />
                            }
                            size="sm"
                            className="flex-1"
                          />
                          <Input
                            type="date"
                            placeholder="End date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            startContent={
                              <FontAwesomeIcon
                                icon={faCalendar}
                                className="text-neutral-400 text-sm"
                              />
                            }
                            size="sm"
                            className="flex-1"
                          />
                        </div>
                      </div>

                      {responses.length === 0 ? (
                        <div className="text-center py-4">
                          <p className="text-sm text-neutral-500">
                            No responses found for the selected metric and date
                            range.
                          </p>
                        </div>
                      ) : (
                        <>
                          <Select
                            placeholder="Search and select response data"
                            selectionMode="multiple"
                            selectedKeys={
                              new Set(editedData["Related Response Data"])
                            }
                            onSelectionChange={handleResponseSelection}
                            size="sm"
                            onInputChange={setResponseSearchTerm}
                            inputValue={responseSearchTerm}
                            items={filteredResponses}
                            aria-label="Search and select response data"
                          >
                            {(response) => (
                              <SelectItem
                                key={response.id}
                                value={response.id}
                                textValue={getResponseDisplayName(response)}
                              >
                                {getResponseDisplayName(response)}
                              </SelectItem>
                            )}
                          </Select>
                          {editedData["Related Response Data"].length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {editedData["Related Response Data"].map(
                                (responseId) => {
                                  const response = responses.find(
                                    (r) => r.id === responseId
                                  );
                                  return (
                                    <Chip
                                      key={responseId}
                                      size="sm"
                                      variant="flat"
                                      color="primary"
                                    >
                                      {response
                                        ? getResponseDisplayName(response)
                                        : `Response ${responseId}`}
                                    </Chip>
                                  );
                                }
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </ModalBody>
              <ModalFooter>
                <StandardCancelButton onPress={onClose}>
                  Cancel
                </StandardCancelButton>
                <Button
                  color="primary"
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 font-medium"
                  onClick={() => submitHandler(onClose)}
                  isLoading={isLoading}
                >
                  Update
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default EditEvidenceModal; 